import {
  isActiveOsJobStatus,
  type OperatingSystemJob,
  type OperatingSystemJobEvent,
} from "@/lib/operating-systems";
import {
  sanitizeProviderPublicMarkdown,
  sanitizeProviderPublicText,
} from "../../lib/providerPublicText";

type ProviderChargeStatus =
  | "charged"
  | "zero_cost"
  | "insufficient_credits"
  | "failed"
  | "skipped_duplicate"
  | "skipped_missing_usage"
  | "not_chargeable"
  | "pending";

const PUBLIC_ROUTER_ACTIVITY_EVENTS = new Set([
  "router_response",
  "router_clarification",
  "router_rejected",
  "agentic_flow_suggestions",
]);

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

export function isAgenticFlowActivityEvent(event: OperatingSystemJobEvent): boolean {
  const metadata = asRecord(event.public_metadata);
  return event.event_type === "agentic_flow_suggestions" ||
    !!metadata.agentic_flow_continuation ||
    !!metadata.agentic_flow_provenance ||
    !!metadata.agentic_flow_activity_identity;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function publicRouterDecisionId(event: OperatingSystemJobEvent): string | null {
  return asString(asRecord(event.public_metadata).router_decision_id);
}

function publicRouterReplyToDecisionId(event: OperatingSystemJobEvent): string | null {
  return asString(asRecord(event.public_metadata).reply_to_decision_id);
}

function publicAgenticFlowDiscoveryId(event: OperatingSystemJobEvent): string | null {
  const metadata = asRecord(event.public_metadata);
  if (event.event_type === "agentic_flow_suggestions") {
    return asString(metadata.discovery_id);
  }
  const continuation = asRecord(metadata.agentic_flow_continuation);
  const provenance = asRecord(metadata.agentic_flow_provenance);
  return asString(continuation.discovery_id) ?? asString(provenance.discovery_id);
}

function publicAgenticFlowActivityIdentity(event: OperatingSystemJobEvent): {
  operationId: string | null;
  discoveryId: string | null;
  clarificationDecisionId: string | null;
} {
  const identity = asRecord(asRecord(event.public_metadata).agentic_flow_activity_identity);
  return {
    operationId: asString(identity.operation_id),
    discoveryId: asString(identity.discovery_id),
    clarificationDecisionId: asString(identity.clarification_decision_id),
  };
}

function safeActivityTimestamp(event: OperatingSystemJobEvent): number | null {
  const observedAt = Date.parse(event.observed_at);
  if (Number.isFinite(observedAt)) return observedAt;
  const createdAt = Date.parse(event.created_at);
  return Number.isFinite(createdAt) ? createdAt : null;
}

function activityTimestamp(event: OperatingSystemJobEvent): number {
  return safeActivityTimestamp(event) ?? 0;
}

function isAtOrBeforeActivityEvent(
  candidate: OperatingSystemJobEvent,
  event: OperatingSystemJobEvent,
): boolean {
  const candidateTimestamp = safeActivityTimestamp(candidate);
  const eventTimestamp = safeActivityTimestamp(event);
  return candidateTimestamp !== null && eventTimestamp !== null && candidateTimestamp <= eventTimestamp;
}

function publicProviderClarificationEventId(event: OperatingSystemJobEvent): string | null {
  return asString(asRecord(event.public_metadata).clarification_event_id);
}

function publicProviderDialogEventId(event: OperatingSystemJobEvent): string | null {
  return asString(asRecord(event.public_metadata).provider_dialog_event_id);
}

function publicSubmissionState(event: OperatingSystemJobEvent): string | null {
  return asString(asRecord(event.public_metadata).submission_state);
}

function isVisibleProviderMessageResponse(event: OperatingSystemJobEvent): boolean {
  return event.event_type === "provider_message_response" &&
    ["queued", "submitting", "submitted"].includes(publicSubmissionState(event) ?? "");
}

function publicClarificationState(event: OperatingSystemJobEvent): string | null {
  return asString(asRecord(event.public_metadata).clarification_state);
}

function publicDialogState(event: OperatingSystemJobEvent): string | null {
  return asString(asRecord(event.public_metadata).dialog_state);
}

function publicDialogKey(event: OperatingSystemJobEvent): string {
  return asString(asRecord(event.public_metadata).dialog_key) ?? event.id;
}

function asNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function formatCreditAmount(value: number | null): string {
  if (value === null) return "";
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, "");
}

export function providerText(value?: string | null): string {
  return sanitizeProviderPublicText(value) ?? "";
}

export function providerMarkdown(value?: string | null): string {
  return sanitizeProviderPublicMarkdown(value) ?? "";
}

export function isUserEvent(event: OperatingSystemJobEvent): boolean {
  return event.source === "base44_editor" && event.event_type === "workspace_user_message" ||
    event.source === "applyt" &&
    (event.event_type === "change_request" ||
      event.event_type === "router_request" ||
      (event.event_type === "provider_clarification_answer" && publicSubmissionState(event) === "submitted") ||
      (event.event_type === "provider_dialog_response" && publicSubmissionState(event) === "submitted") ||
      isVisibleProviderMessageResponse(event));
}

/**
 * A user message that appears in the Provider transcript only after its App
 * job completed is a direct follow-up, not the build instruction that started
 * the job. It is safe to mirror as a user message in the App activity.
 */
export function isVisiblePostCompletionProviderUserMessage(
  event: OperatingSystemJobEvent,
  job: Pick<OperatingSystemJob, "id" | "status" | "completed_at"> | null | undefined,
  events: readonly OperatingSystemJobEvent[] = [],
): boolean {
  const isProviderFollowUp = event.source === "base44_editor" &&
    event.event_type === "editor_message" &&
    event.role?.toLowerCase() === "user" &&
    isPostCompletionProviderClarification(event, job);
  if (!isProviderFollowUp) return false;

  const content = providerText(event.content).replace(/\s+/g, " ").trim().toLowerCase();
  const observedAt = new Date(event.observed_at).getTime();
  return !events.some((candidate) => {
    if (candidate.source !== "applyt" || !["change_request", "router_request"].includes(candidate.event_type)) {
      return false;
    }
    const candidateContent = providerText(candidate.content).replace(/\s+/g, " ").trim().toLowerCase();
    const candidateTime = new Date(candidate.observed_at).getTime();
    return candidateContent === content && Number.isFinite(candidateTime) && Number.isFinite(observedAt) &&
      Math.abs(observedAt - candidateTime) <= 5 * 60 * 1000;
  });
}

/**
 * Resolves provider questions that have a safe, persisted answered marker.
 * Provider tool identifiers and raw payloads remain server-only.
 */
export function answeredProviderClarificationEventIds(
  events: readonly OperatingSystemJobEvent[],
): Set<string> {
  const answeredIds = new Set<string>();
  for (const event of events) {
    if (
      event.source === "base44_editor" &&
      event.event_type === "clarifying_questions" &&
      publicClarificationState(event) === "answered"
    ) {
      answeredIds.add(event.id);
      continue;
    }
    if (
      event.source === "applyt" &&
      event.event_type === "provider_clarification_answer" &&
      publicSubmissionState(event) === "submitted"
    ) {
      const clarificationEventId = publicProviderClarificationEventId(event);
      if (clarificationEventId) answeredIds.add(clarificationEventId);
    }
  }
  return answeredIds;
}

export function latestWaitingProviderClarificationEvent(
  events: readonly OperatingSystemJobEvent[],
  latestJobId: string | null | undefined,
): OperatingSystemJobEvent | null {
  if (!latestJobId) return null;
  const answeredIds = answeredProviderClarificationEventIds(events);
  return [...events]
    .filter((event) =>
      event.source === "base44_editor" &&
      event.event_type === "clarifying_questions" &&
      event.job_id === latestJobId &&
      !answeredIds.has(event.id) &&
      !["answered", "closed"].includes(publicClarificationState(event) ?? "")
    )
    .sort((left, right) => {
      const observed = new Date(right.observed_at).getTime() - new Date(left.observed_at).getTime();
      if (observed) return observed;
      return new Date(right.created_at).getTime() - new Date(left.created_at).getTime();
    })[0] ?? null;
}

export function isPostCompletionProviderClarification(
  event: OperatingSystemJobEvent | null | undefined,
  job: Pick<OperatingSystemJob, "id" | "status" | "completed_at"> | null | undefined,
): boolean {
  if (!event || !job || job.status !== "completed" || event.job_id !== job.id || !job.completed_at) {
    return false;
  }
  const observedAt = new Date(event.observed_at).getTime();
  const completedAt = new Date(job.completed_at).getTime();
  return Number.isFinite(observedAt) && Number.isFinite(completedAt) && observedAt > completedAt;
}

export function resolvedProviderDialogEventIds(
  events: readonly OperatingSystemJobEvent[],
): Set<string> {
  const resolvedIds = new Set<string>();
  for (const event of events) {
    if (
      event.source === "base44_editor" &&
      event.event_type === "provider_dialog" &&
      ["answered", "dismissed", "closed"].includes(publicDialogState(event) ?? "")
    ) {
      resolvedIds.add(event.id);
      continue;
    }
    if (
      event.source === "applyt" &&
      event.event_type === "provider_dialog_response" &&
      publicSubmissionState(event) === "submitted"
    ) {
      const dialogEventId = publicProviderDialogEventId(event);
      if (dialogEventId) resolvedIds.add(dialogEventId);
    }
  }
  return resolvedIds;
}

/**
 * Returns the newest safe Provider dialog that still needs a response for the
 * App's current job. This is deliberately based only on public event state so
 * callers can use it to surface the edit panel without exposing audit data.
 */
export function latestWaitingProviderDialogEvent(
  events: readonly OperatingSystemJobEvent[],
  latestJobId: string | null | undefined,
): OperatingSystemJobEvent | null {
  if (!latestJobId) return null;
  const resolvedIds = resolvedProviderDialogEventIds(events);
  const sourceOrderedDialogs = [...events]
    .filter((event) =>
      event.source === "base44_editor" &&
      event.event_type === "provider_dialog" &&
      event.job_id === latestJobId
    )
    .sort((left, right) => {
      const observed = new Date(left.observed_at).getTime() - new Date(right.observed_at).getTime();
      if (observed) return observed;
      return new Date(left.created_at).getTime() - new Date(right.created_at).getTime();
    });
  const latestByDialogKey = new Map<string, OperatingSystemJobEvent>();
  for (const event of sourceOrderedDialogs) latestByDialogKey.set(publicDialogKey(event), event);

  return [...latestByDialogKey.values()]
    .filter((event) =>
      !resolvedIds.has(event.id) &&
      ["waiting", "authorizing"].includes(publicDialogState(event) ?? "")
    )
    .at(-1) ?? null;
}

/**
 * Returns clarification decision IDs that have a later, persisted user reply.
 * Only the explicitly safe activity metadata is considered; audit payloads are
 * deliberately excluded from the App activity UI.
 */
export function answeredRouterClarificationDecisionIds(
  events: readonly OperatingSystemJobEvent[],
): Set<string> {
  const clarificationIds = new Set<string>();
  const answeredIds = new Set<string>();

  for (const event of events) {
    if (event.source !== "applyt") continue;
    if (event.event_type === "router_clarification") {
      const decisionId = publicRouterDecisionId(event);
      if (decisionId) clarificationIds.add(decisionId);
      continue;
    }
    if (event.event_type === "router_request") {
      const replyToDecisionId = publicRouterReplyToDecisionId(event);
      if (replyToDecisionId && clarificationIds.has(replyToDecisionId)) {
        answeredIds.add(replyToDecisionId);
      }
    }
  }

  return answeredIds;
}

/**
 * Projects the currently usable Agentic Flow discovery and clarification from
 * append-only activity. Historical questions stay visible, but a question from
 * an older discovery (or an unscoped question that predates the current
 * discovery) is never treated as the live setup operation.
 */
export function currentAgenticFlowActivity(events: readonly OperatingSystemJobEvent[]): {
  activeDiscoveryId: string | null;
  actionableClarificationDecisionId: string | null;
  actionableClarificationOperationId: string | null;
} {
  const ordered = [...events].sort((left, right) => {
    const timestampDifference = activityTimestamp(left) - activityTimestamp(right);
    if (timestampDifference) return timestampDifference;
    return left.id.localeCompare(right.id);
  });
  const latestDiscoveryEvent = [...ordered].reverse().find((event) =>
    event.source === "applyt" &&
    event.event_type === "agentic_flow_suggestions" &&
    !!publicAgenticFlowDiscoveryId(event)
  );
  const activeDiscoveryId = latestDiscoveryEvent
    ? publicAgenticFlowDiscoveryId(latestDiscoveryEvent)
    : null;
  const latestDiscoveryTimestamp = latestDiscoveryEvent
    ? activityTimestamp(latestDiscoveryEvent)
    : 0;
  const answeredIds = answeredRouterClarificationDecisionIds(ordered);
  const actionableClarification = [...ordered].reverse().find((event) => {
    if (event.source !== "applyt" || event.event_type !== "router_clarification") return false;
    const decisionId = publicRouterDecisionId(event);
    if (!decisionId || answeredIds.has(decisionId)) return false;
    const discoveryId = publicAgenticFlowDiscoveryId(event);
    if (discoveryId) return discoveryId === activeDiscoveryId;
    return activityTimestamp(event) >= latestDiscoveryTimestamp;
  });

  const actionableClarificationDecisionId = actionableClarification
    ? publicRouterDecisionId(actionableClarification)
    : null;
  const matchingRequestIdentity = actionableClarificationDecisionId && activeDiscoveryId
    ? [...ordered].reverse().find((event) => {
      if (event.source !== "applyt" || event.event_type !== "router_request") return false;
      const identity = publicAgenticFlowActivityIdentity(event);
      return !!(
      identity.operationId &&
      identity.discoveryId === activeDiscoveryId &&
      identity.clarificationDecisionId === actionableClarificationDecisionId &&
      isAtOrBeforeActivityEvent(event, actionableClarification)
      );
    })
    : null;

  return {
    activeDiscoveryId,
    actionableClarificationDecisionId,
    // This identity is deliberately sourced from the preceding companion user
    // request, never from the question being evaluated, so a stale
    // continuation cannot make itself actionable merely by carrying a
    // non-empty operation id. Events without a safe ordering timestamp fail
    // closed above.
    actionableClarificationOperationId: matchingRequestIdentity
      ? publicAgenticFlowActivityIdentity(matchingRequestIdentity).operationId
      : null,
  };
}

function isCurrentPreJobRouterClarification(
  event: OperatingSystemJobEvent,
  events: readonly OperatingSystemJobEvent[],
  decisionId: string,
): boolean {
  if (event.job_id) return false;
  const continuation = asRecord(asRecord(event.public_metadata).agentic_flow_continuation);
  const activity = currentAgenticFlowActivity(events);
  return !!activity.activeDiscoveryId &&
    activity.actionableClarificationDecisionId === decisionId &&
    asString(continuation.discovery_id) === activity.activeDiscoveryId &&
    asString(continuation.clarification_decision_id) === decisionId &&
    asString(continuation.operation_id) === activity.actionableClarificationOperationId;
}

/**
 * Keeps an App's current router question visibly pending while the separate
 * current-job projection is loading. Job-bound questions require an exact
 * active-job match; pre-job Agentic Flow questions instead carry their exact
 * continuation and decision identity before a job exists.
 */
export function routerClarificationAssociationState({
  event,
  events,
  latestJobId,
  job,
}: {
  event: OperatingSystemJobEvent;
  events: readonly OperatingSystemJobEvent[];
  latestJobId: string | null | undefined;
  job: OperatingSystemJob | null;
}): "actionable" | "loading" | "historical" {
  const decisionId = publicRouterDecisionId(event);
  if (event.source !== "applyt" || event.event_type !== "router_clarification" || !decisionId) {
    return "historical";
  }

  if (!event.job_id) {
    return isCurrentPreJobRouterClarification(event, events, decisionId)
      ? "actionable"
      : "historical";
  }

  if (!latestJobId || event.job_id !== latestJobId) return "historical";

  if (!job || job.id !== latestJobId) return "loading";
  if (!isActiveOsJobStatus(job.status)) return "historical";

  return currentAgenticFlowActivity(events).actionableClarificationDecisionId === decisionId
    ? "actionable"
    : "historical";
}

export function sourceLabel(event: OperatingSystemJobEvent): string {
  if (isUserEvent(event)) return "You";
  if (event.source === "applyt") return "Applyt";
  switch (event.source) {
    case "base44_editor":
    case "base44_mcp":
    case "base44_usage":
      return "Applyt";
    default:
      return "System";
  }
}

function providerChargeStatus(event: OperatingSystemJobEvent): ProviderChargeStatus | null {
  const status = asString(asRecord(event.raw_json).charge_status);
  if (
    status === "charged" ||
    status === "zero_cost" ||
    status === "insufficient_credits" ||
    status === "failed" ||
    status === "skipped_duplicate" ||
    status === "skipped_missing_usage" ||
    status === "not_chargeable" ||
    status === "pending"
  ) {
    return status;
  }
  return null;
}

function isProviderChargeAuditEvent(event: OperatingSystemJobEvent): boolean {
  return event.source === "base44_usage" && event.event_type === "provider_cost_delta";
}

function isVisibleProviderChargeEvent(event: OperatingSystemJobEvent): boolean {
  const status = providerChargeStatus(event);
  return status === "charged" || status === "insufficient_credits";
}

function isVisibleProviderAssistantMessage(event: OperatingSystemJobEvent): boolean {
  return event.source === "base44_editor" &&
    (
      event.event_type === "editor_message" ||
      event.event_type === "clarifying_questions" ||
      event.event_type === "provider_dialog"
    ) &&
    !!event.content?.trim() &&
    event.role?.toLowerCase() === "assistant";
}

export function isCreditEvent(event: OperatingSystemJobEvent): boolean {
  if (isProviderChargeAuditEvent(event)) return isVisibleProviderChargeEvent(event);
  const text = `${event.event_type} ${event.content ?? ""}`.toLowerCase();
  return event.source === "base44_usage" && /credit|free plan|daily credits/.test(text);
}

export function isCreditLimitEvent(event: OperatingSystemJobEvent): boolean {
  if (event.event_type === "credit_limit") return event.source === "base44_usage";
  if (isProviderChargeAuditEvent(event)) return false;
  return event.source === "base44_usage" &&
    /out of|credits? (?:are|is) exhausted|credit limit|0 credits remaining/i.test(event.content ?? "");
}

export function isVisibleActivityEvent(event: OperatingSystemJobEvent): boolean {
  if (event.event_type === "base44_prompt") return false;
  if (event.event_type === "openai_prep_cost") return false;
  if (["suggestions_sync_empty", "suggestions_sync_failed"].includes(event.event_type)) return false;
  if (
    event.source === "base44_editor" &&
    ["workspace_user_message", "workspace_activity", "suggestions"].includes(event.event_type)
  ) return true;
  if (isVisibleProviderAssistantMessage(event)) return true;
  if (event.source === "base44_editor") return false;
  if (event.source === "applyt") {
    return event.event_type === "change_request" ||
      event.event_type === "router_request" ||
      (event.event_type === "provider_clarification_answer" && publicSubmissionState(event) === "submitted") ||
      (event.event_type === "provider_dialog_response" && publicSubmissionState(event) === "submitted") ||
      isVisibleProviderMessageResponse(event) ||
      PUBLIC_ROUTER_ACTIVITY_EVENTS.has(event.event_type);
  }
  if (isProviderChargeAuditEvent(event)) return isVisibleProviderChargeEvent(event);
  if (event.event_type === "credit_limit") return isCreditLimitEvent(event);
  if (event.event_type === "credit_snapshot") return false;
  if (event.source === "base44_usage") return isCreditLimitEvent(event);
  if (["job_created", "history_sync_empty", "credit_sync_empty", "history_sync_failed"].includes(event.event_type)) {
    return false;
  }
  return false;
}

export function hasBlockingCreditEvent(events: OperatingSystemJobEvent[]): boolean {
  return events.some(isCreditLimitEvent);
}

export function displayEventContent(event: OperatingSystemJobEvent): string {
  const content = event.source === "applyt" && ["change_request", "router_request"].includes(event.event_type)
    ? event.content ?? ""
    : event.source === "base44_editor" && ["editor_message", "workspace_user_message"].includes(event.event_type)
    ? providerMarkdown(event.content)
    : providerText(event.content).trim();
  if (!isProviderChargeAuditEvent(event)) return content;

  const status = providerChargeStatus(event);
  if (status !== "charged" && status !== "insufficient_credits") return content;

  const raw = asRecord(event.raw_json);
  const requestKind = asString(raw.request_kind) ?? asString(raw.job_type);
  const action = requestKind === "update" ? "App update" : "App creation";
  const totalCost = asNumber(raw.total_cost_cents);
  const amount = formatCreditAmount(totalCost);
  const suffix = amount ? `${amount} credits` : "credits";

  return status === "insufficient_credits"
    ? `${action} cost: ${suffix}; balance was insufficient.`
    : `${action} cost: ${suffix}.`;
}
